import {AfterViewInit, Component, ElementRef, Input, ViewChild} from '@angular/core';
import {Table} from '../member-manager/member-manager.component';
import {FieldType, MemberAddeditComponent, MemberAddeditDialogModel} from '../member-addedit/member-addedit.component';
import {SnackBarComponent} from '../../snackbar/snackbar.component';
import {UsersInsuranceService} from '../../../services/usersinsurance/users.insurance.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSelectChange} from '@angular/material/select';
import {fromEvent} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, tap} from 'rxjs/operators';
import {enrollAddedFields} from '../client-enrollment/client-enrollment.component';
import {MatDialog} from '@angular/material/dialog';
import * as _ from 'lodash';
import {TeamCompaniesService} from '../../../services/teamcompanies/teamcompanies.service';
import {VendorProductsService} from '../../../services/vendorproducts/vendorproducts.service';
import {AppSettings} from '../../../app.settings';
import {UiAlertService} from '../../../services/ui-alert/ui-alert.service';
import {SessionService} from '../../../services/session/session.service';
import {buildClientQleFields, toClientQleRequest} from '../client-qle/client-qle-fields';
import {UtilsService} from '../../../services/utils/utils.service';

@Component({
  selector: 'app-client-eligibility',
  templateUrl: './client-eligibility.component.html',
  styleUrls: ['./client-eligibility.component.scss']
})
export class ClientEligibilityComponent implements AfterViewInit{

  @Input() clientId = '';
  @Input() clientAdministrator = false;
  @ViewChild('search') search: ElementRef | undefined;

  public reload = false;
  public itemCount = 0;

  public table: Table = {
    title: 'Eligible Member',
    addEditFields: [
      {name: 'Given Name', label: 'First Name', fieldType: FieldType.F_STRING, required: true},
      {name: 'Family Name', label: 'Last Name', fieldType: FieldType.F_STRING, required: true},
      {name: 'SSN', label: 'Social Security Number', fieldType: FieldType.F_SSN, required: true},
      {name: 'Product', label: 'Product', fieldType: FieldType.F_DROPDOWN, values: [], required: false},
      {name: 'Start Date', label: 'Enrollment Start Date', fieldType: AppSettings.isQa ? FieldType.F_ANYDATE : FieldType.F_FUTUREDATE, required: false},
      {name: 'End Date', label: 'Enrollment End Date', fieldType: AppSettings.isQa ? FieldType.F_ANYDATE : FieldType.F_FUTUREDATE, required: false},
    ],
    columnsToDisplay: [
      {
        data: (row: { [x: string]: any; }) => row['Given Name'],
        label: 'First Name',
      },
      {
        data: (row: { [x: string]: any; }) => row['Family Name'],
        label: 'Last Name'
      },
      {
        data: (row: { [x: string]: any; }) => row.SSN,
        label: 'SSN'
      },
      {
        data: (row: any) => this.utilsService.vendorProductSlugToDisplay(row.Product || row._product),
        label: 'Product'
      },
      {
        data: (row: any) => row['Start Date'] || '',
        label: 'Eligibility Start'
      },
      {
        data: (row: any) => row['End Date'] || '',
        label: 'Eligibility End'
      },
      {
        data: (row: any) => row.metadata?.eligibility?.eventType === 'new_hire'
          ? 'New Hire'
          : row.metadata?.eligibility?.eventType === 'qualifying_life_event'
            ? 'Qualifying Life Event'
            : '',
        label: 'Event Type'
      },
      {
        data: (row: { [x: string]: any; }) => row.state + (row.metadata.deletepending ? ', pending delete' : ''),
        label: 'State'
      }
    ],
    menuItems: [
      {
        name: 'Make Ineligible',
        hidden: () => this.clientAdministrator,
        disabled: this.ineligibleDisabled.bind(this),
        click: this.makeIneligible.bind(this),
      },
      {
        name: 'Enroll',
        hidden: () => this.clientAdministrator,
        disabled: this.enrollDisabled.bind(this),
        click: this.enroll.bind(this),
      },
      {
        name: 'Edit Eligibility',
        hidden: () => !this.clientAdministrator && !this.sessionService.isExactSuperAdmin(),
        click: this.editClientEligibility.bind(this),
      },
      {
        name: 'Make Ineligible',
        hidden: () => !this.clientAdministrator,
        click: this.makeClientIneligible.bind(this),
      },
    ],
    templateUrl: '/assets/files/Wallit-eligibility.xlsx',
    templateFileName: 'Wallit-eligibility.xlsx',
    where: {
      limit: 50,
      offset: 0,
      sort: '',
      filter: {}
    }
  };

  constructor(
      private usersInsuranceService: UsersInsuranceService,
      private snackBar: MatSnackBar,
      private dialog: MatDialog,
      private teamCompaniesService: TeamCompaniesService,
      private vendorProductsService: VendorProductsService,
      private uiAlertService: UiAlertService,
      private sessionService: SessionService,
      private utilsService: UtilsService,
  ) {
    this.usersInsuranceService.insuranceStateCountObservable.subscribe(result => {
      this.itemCount = result.eligible;
    });
  }

  ngAfterViewInit(): void {
    this.setProductField(this.table.addEditFields);
    fromEvent(this.search?.nativeElement, 'keyup').pipe(
        filter(Boolean),
        debounceTime(300),
        distinctUntilChanged(),
        tap((): void => {
          Object.assign(this.table.where.filter, {search: this.search?.nativeElement.value.trim().toLowerCase()});
          this.reload = !this.reload;
    })).subscribe();
  }

  enrollDisabled(item: any): boolean {
    return item.state !== 'eligible';
  }

  ineligibleDisabled(item: any): boolean {
    return item.state !== 'eligible' || item.metadata.deletepending;
  }

  makeIneligible(item: any): Promise<boolean> {
    return this.usersInsuranceService.modifyInsuranceState(item.id, {state: 'census'}).then(() => {
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Member set to ineligible' });
      return true;
    });
  }

  editClientEligibility(item: any): Promise<boolean> {
    return this.usersInsuranceService.getClientEligibilityProducts(this.clientId).then(products => {
      const selectedProduct = products.find(product => product.product === item._product);
      if (!selectedProduct) {
        this.snackBar.openFromComponent(SnackBarComponent, {data: 'This product is not configured for the employer'});
        return false;
      }
      const fields = buildClientQleFields(products, false);
      fields[0].values = [`${selectedProduct.label}:${selectedProduct.id}`];
      const upload = item.metadata?.upload || {};
      const data = {
        Products: selectedProduct.id,
        'Start Date': upload['Start Date'],
        'End Date': upload['End Date'],
        'Event Type': item.metadata?.eligibility?.eventType || upload['Eligibility Event'],
        Note: item.metadata?.eligibility?.note || upload['Eligibility Note'] || '',
      };
      const dialogRef = this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel(data, fields, 'QLE Eligibility', true, 'Edit', true),
      });
      return new Promise(resolve => {
        dialogRef.afterClosed().subscribe((dialogResult: any) => {
          if (!dialogResult) {
            resolve(false);
            return;
          }
          this.uiAlertService.presentAlertConfirm('Update this product eligibility window?').then(confirmed => {
            if (!confirmed) {
              resolve(false);
              return;
            }
            this.usersInsuranceService
              .setClientQleEligibility(this.clientId, item.id, toClientQleRequest(dialogResult))
              .then(() => {
                this.snackBar.openFromComponent(SnackBarComponent, {data: 'Eligibility updated'});
                resolve(true);
              })
              .catch(error => {
                const message = error?.data?.error || error?.message || 'Unable to update eligibility';
                this.snackBar.openFromComponent(SnackBarComponent, {data: message});
                resolve(false);
              });
          });
        });
      });
    });
  }

  makeClientIneligible(item: any): Promise<boolean> {
    return this.uiAlertService.presentAlertConfirm(
      'Make this member ineligible for this product?'
    ).then(confirmed => {
      if (!confirmed) {
        return false;
      }
      return this.usersInsuranceService.makeClientProductIneligible(this.clientId, item.id).then(() => {
        this.snackBar.openFromComponent(SnackBarComponent, {data: 'Product eligibility removed'});
        return true;
      }).catch(error => {
        const message = error?.data?.error || error?.message || 'Unable to remove product eligibility';
        this.snackBar.openFromComponent(SnackBarComponent, {data: message});
        return false;
      });
    });
  }

  setProductField(fields: any): Promise<void> {
    if (this.clientAdministrator) {
      return this.usersInsuranceService.getClientEligibilityProducts(this.clientId).then(products => {
        const productTableField = _.find(fields, field => field.name === 'Product');
        if (productTableField) {
          productTableField.values = products.map(product => `${product.label}:${product.product}`);
        }
      });
    }
    return this.teamCompaniesService.getCompany(this.clientId).then(company => {
      return this.vendorProductsService.getAllVendorProducts().then(products => {
        const availableProductIds = this.teamCompaniesService.getAvailableProductIds(company);
        products = products.filter((product: { id: string; }) => availableProductIds.find((productId: string) => productId === product.id));
        const productTableField = _.find(fields, field => field.name === 'Product');
        // @ts-ignore
        productTableField.values = products.map((product: any) => `${product.vendorName} ${product.productName}:${product.vendorSlug}-${product.productSlug}`);
      });
    });
  }

  enroll(item: any): Promise<boolean> {
    return this.setProductField(enrollAddedFields).then(() => {
      const dialogRef = this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel(item, enrollAddedFields, 'Required Enrollment Fields')
      });
      return new Promise((resolve, reject) => {
        dialogRef.afterClosed().subscribe((dialogResult: any) => {
          if (dialogResult) {
            item.metadata.upload = _.merge({}, item.metadata.upload, dialogResult);
            this.usersInsuranceService.modifyInsuranceState(item.id, {state: 'enrolled', metadata: item.metadata}).then(() => {
              this.snackBar.openFromComponent(SnackBarComponent, {data: 'Member set to enrolled'});
              resolve(true);
            });
          } else {
            resolve(false);
          }
        });
      });
    });
  }

  setStateFilter(event: MatSelectChange): void {
    Object.assign(this.table.where.filter, {state: event.value});
    this.reload = !this.reload;
  }

  tableUpdated(count: number): void {
    this.itemCount = count;
  }

  pageEvent(event: any): void {
    this.table.where.limit = event.pageSize;
    this.table.where.offset = event.pageIndex * event.pageSize;
    this.reload = !this.reload;
  }

}
